import {
  fetchSubscriptionHealth,
  fetchTenantContextOverview,
  fetchTenants,
  type SubscriptionHealthDto,
  type TenantContextOverview,
  type TenantSummary,
} from '@/lib/tenants';
import { fetchCertificates } from '@/lib/api';
import type { HomeData } from '@/components/home/home-page';
import type { CertStatus } from '@/components/certificates/certificates-table';
import type { OperatorHomeData } from '@/components/home/operator-home';

const DAY = 86_400_000;

export function initialsOf(name: string): string {
  const w = name.trim().split(/\s+/).filter(Boolean);
  if (!w.length) return '?';
  if (w.length === 1) return w[0].slice(0, 2).toUpperCase();
  return (w[0][0] + w[w.length - 1][0]).toUpperCase();
}

const fmtDate = (iso?: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : '—';

/** Expiry drives status; the API has no explicit revoked flag on the list rows. */
export function certStatus(expiresAt?: string | null, status?: string | null): CertStatus {
  if (status && status.toLowerCase() === 'revoked') return 'Revoked';
  if (!expiresAt) return 'Valid';
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (ms < 0) return 'Expired';
  if (ms < 30 * DAY) return 'Expiring soon';
  return 'Valid';
}

const statusFromHealth = (h: SubscriptionHealthDto | null): HomeData['tenant']['subscriptionStatus'] => {
  switch (h?.status) {
    case 'expired':
    case 'blocked':
      return 'expired';
    case 'grace_period':
      return 'past_due';
    case 'canceled_active':
    case 'active':
      return h?.billingStatus === 'TRIALING' ? 'trialing' : 'active';
    default:
      return 'active';
  }
};

type CertRow = {
  id: number | string;
  name: string;
  competency: string;
  issued?: string | null;
  expiry?: string | null;
  status?: string | null;
  emailSentAt?: string | null;
};

/**
 * Assembles the tenant home from three existing endpoints. The API exposes no
 * "needs attention" resource, so those rows are derived client-side from the
 * most recent certificates plus the subscription meters.
 */
export async function loadHomeData(tenantName: string): Promise<HomeData> {
  const [overview, health, certPage] = await Promise.all([
    fetchTenantContextOverview().catch(() => null as TenantContextOverview | null),
    fetchSubscriptionHealth().catch(() => null as SubscriptionHealthDto | null),
    fetchCertificates(1, 25, undefined, { scope: 'me' }).catch(() => ({ data: [], total: 0 })),
  ]);

  const sub = overview?.subscription ?? null;
  const rows: CertRow[] = Array.isArray(certPage?.data) ? certPage.data : [];

  const recent = rows.slice(0, 10).map((c) => ({
    id: String(c.id),
    name: c.name ?? '—',
    competency: c.competency,
    issuedAt: fmtDate(c.issued),
    status: certStatus(c.expiry, c.status),
    emailSent: Boolean(c.emailSentAt),
  }));

  const attention: HomeData['attention'] = [];
  const expiring = rows.filter((c) => certStatus(c.expiry, c.status) === 'Expiring soon');
  if (expiring.length) {
    attention.push({
      id: 'expiring',
      kind: 'expiring',
      title: `${expiring.length} certificate${expiring.length === 1 ? '' : 's'} expiring within 30 days`,
      meta: expiring.slice(0, 3).map((c) => c.name ?? '—').join(', '),
      ctaLabel: 'Review',
      href: '/certificates',
    });
  }
  const unsent = rows.filter((c) => !c.emailSentAt);
  if (unsent.length) {
    attention.push({
      id: 'email',
      kind: 'email',
      title: `${unsent.length} issuance email${unsent.length === 1 ? '' : 's'} not sent`,
      meta: 'Recipients have not been notified yet',
      ctaLabel: 'Send',
      href: '/certificates',
    });
  }
  if (sub && sub.usedCertificates > sub.includedCertificates) {
    attention.push({
      id: 'limit',
      kind: 'limit',
      title: 'Certificate allowance exceeded',
      meta: `${sub.usedCertificates - sub.includedCertificates} above the included ${sub.includedCertificates}`,
      ctaLabel: 'View plan',
      href: '/tenant/subscription',
    });
  }
  if (sub && sub.usedSeats > sub.includedSeats) {
    attention.push({
      id: 'seats',
      kind: 'limit',
      title: 'Seat allowance exceeded',
      meta: `${sub.usedSeats - sub.includedSeats} above the included ${sub.includedSeats}`,
      ctaLabel: 'View plan',
      href: '/tenant/subscription',
    });
  }

  const name = overview?.name ?? tenantName;

  return {
    tenant: {
      name,
      initials: initialsOf(name),
      planName: sub?.planName ?? health?.planName ?? 'No plan',
      subscriptionStatus: statusFromHealth(health),
      renewalDate: fmtDate(health?.subscriptionExpiresAt),
    },
    // TenantContextOverview carries no pricing; the figures live on the
    // operator-scoped TenantSummary. Shown on Plan & billing instead.
    cost: { estimatedLabel: '', breakdownLabel: '' },
    meters: {
      certificates: {
        label: 'Certificates',
        used: sub?.usedCertificates ?? overview?.certificateCount ?? 0,
        included: sub?.includedCertificates ?? 0,
        max: sub?.certificateLimit ?? 0,
        overageUnitLabel: 'per certificate',
        unitNoun: 'certificates',
      },
      seats: {
        label: 'Seats',
        used: sub?.usedSeats ?? overview?.userCount ?? 0,
        included: sub?.includedSeats ?? 0,
        max: sub?.maxSeats ?? sub?.seatLimit ?? 0,
        overageUnitLabel: 'per seat / month',
        unitNoun: 'seats',
      },
    },
    recent,
    totalCertificates: certPage?.total ?? overview?.certificateCount ?? 0,
    attention,
  };
}

/** Platform home. Derived entirely from the tenant list the operator already loads. */
export async function loadOperatorHomeData(): Promise<OperatorHomeData> {
  const tenants: TenantSummary[] = await fetchTenants();
  const soon = (iso: string | null) => {
    if (!iso) return false;
    const ms = new Date(iso).getTime() - Date.now();
    return ms > 0 && ms < 30 * DAY;
  };

  const list: OperatorHomeData['tenants'] = tenants.map((t) => ({
    id: String(t.id),
    name: t.name,
    slug: t.slug,
    plan: t.subscriptionPlan?.name ?? '—',
    users: t.userCount,
    certificates: t.certificateCount,
    contractDeployed: Boolean(t.contractAddress),
    chainName: t.chainName,
    expiresAtLabel: fmtDate(t.subscriptionExpiresAt),
    expiresSoon: soon(t.subscriptionExpiresAt),
  }));

  const attention: OperatorHomeData['attention'] = [];
  for (const t of tenants) {
    if (!t.contractAddress) {
      attention.push({
        id: `contract-${t.id}`,
        kind: 'contract',
        subject: t.name,
        title: 'No contract deployed',
        meta: `${t.slug} · ${t.certificateCount} certificates`,
        ctaLabel: 'Open tenant',
        href: '/admin/tenants',
      });
    }
    const s = t.subscription;
    if (s && s.usedCertificates > s.includedCertificates) {
      attention.push({
        id: `certs-${t.id}`,
        kind: 'allowance',
        subject: t.name,
        title: 'Over certificate allowance',
        meta: `${s.usedCertificates} used of ${s.includedCertificates} included`,
        ctaLabel: 'Open tenant',
        href: '/admin/tenants',
      });
    }
    if (s && s.usedSeats > s.includedSeats) {
      attention.push({
        id: `seats-${t.id}`,
        kind: 'seats',
        subject: t.name,
        title: 'Over seat allowance',
        meta: `${s.usedSeats} used of ${s.includedSeats} included`,
        ctaLabel: 'Open tenant',
        href: '/admin/tenants',
      });
    }
    if (soon(t.subscriptionExpiresAt)) {
      attention.push({
        id: `exp-${t.id}`,
        kind: 'expiry',
        subject: t.name,
        title: 'Subscription expires within 30 days',
        meta: fmtDate(t.subscriptionExpiresAt),
        ctaLabel: 'Open tenant',
        href: '/admin/tenants',
      });
    }
  }
  // NOTE: the "gas" attention kind needs a master-wallet balance the API does
  // not expose on the tenant list; left unpopulated rather than faked.

  const totalCerts = tenants.reduce((n, t) => n + t.certificateCount, 0);
  const deployed = tenants.filter((t) => t.contractAddress).length;
  const chains = new Set(tenants.map((t) => t.chainId).filter(Boolean)).size;

  return {
    metrics: [
      { label: 'Tenants', value: String(tenants.length) },
      {
        label: 'Users',
        value: String(tenants.reduce((n, t) => n + t.userCount, 0)),
      },
      { label: 'Certificates', value: totalCerts.toLocaleString() },
      {
        label: 'Contracts deployed',
        value: `${deployed}/${tenants.length}`,
        delta:
          deployed < tenants.length
            ? `${tenants.length - deployed} awaiting deploy`
            : undefined,
        deltaTone: deployed < tenants.length ? 'warning' : 'positive',
      },
    ],
    attention,
    tenants: list,
    newestTenants: [...tenants]
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .slice(0, 5)
      .map((t) => ({
        id: String(t.id),
        name: t.name,
        meta: fmtDate(t.createdAt),
        href: '/admin/tenants',
      })),
    // The API has no cross-tenant "recent certificates" feed; the operator
    // certificate list is paginated per tenant. Left empty rather than faked.
    newestCertificates: [],
    totals: { tenants: tenants.length, certificates: totalCerts, chains },
  };
}
